import React from 'react';
import { Button, Collapse, Link, makeStyles, Paper, Typography } from '@material-ui/core';
import { HomeOutlined } from '@material-ui/icons';
import { ECriteriaRatingType } from '../../interfaces';
import {
  CriterionValue,
  effectiveValue,
  ICriterionEntry,
  IPartnerCriterion,
  IPartnerGroup,
  overrideCount,
  valueFor,
} from './partnerRatingState';
import { StarsValue, YesNoValue } from './RatingInputs';

const useStyles = makeStyles({
  block: { marginTop: 6 },
  card: { position: 'relative', display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px', borderRadius: 7 },
  flash: {
    position: 'absolute',
    left: -1,
    top: -1,
    bottom: -1,
    width: 4,
    borderRadius: '7px 0 0 7px',
    backgroundColor: '#e03131',
  },
  text: { flex: 1, minWidth: 0 },
  label: { fontSize: 15, fontWeight: 600, color: '#111827' },
  badge: {
    display: 'inline-block',
    marginTop: 6,
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 12,
    color: '#8a5000',
    backgroundColor: '#fdf0d5',
  },
  link: { width: 104, justifyContent: 'flex-end', whiteSpace: 'nowrap' },
  overrides: {
    margin: '6px 0 6px 18px',
    border: '1px solid #dbe3ef',
    borderRadius: 6,
    overflow: 'hidden',
  },
  overridesHead: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', backgroundColor: '#f1f5fb' },
  overridesTitle: { flex: 1, fontSize: 12, color: '#374151' },
  reset: { fontSize: 12, color: '#b42318' },
  overrideRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 14px',
    borderTop: '1px solid #f0f3f8',
  },
  realEstate: { flex: 1, fontSize: 13, color: '#1f2937' },
  changed: { fontSize: 11, color: '#8a5000', backgroundColor: '#fdf0d5', padding: '2px 8px', borderRadius: 9 },
});

const badgeText = (changed: number, total: number, effective: number | null, isStars: boolean) =>
  isStars
    ? `Изменено в ${changed} из ${total} ЖК · итоговая ${effective}`
    : `Изменено в ${changed} из ${total} ЖК · «Нет» в ${effective} ЖК`;

interface IProps {
  id: string;
  group: IPartnerGroup;
  criterion: IPartnerCriterion;
  entry: ICriterionEntry;
  flash: boolean;
  realEstatesOpen: boolean;
  onToggleRealEstates(): void;
  onBase(value: CriterionValue): void;
  onOverride(realEstateGuid: string, value: CriterionValue): void;
  onResetOverrides(): void;
}

const PartnerCriterionCard = ({
  id,
  group,
  criterion,
  entry,
  flash,
  realEstatesOpen,
  onToggleRealEstates,
  onBase,
  onOverride,
  onResetOverrides,
}: IProps) => {
  const classes = useStyles();
  const isStars = criterion.type === ECriteriaRatingType.Mark;
  const changed = overrideCount(entry);
  const effective = effectiveValue(entry, group, criterion);
  const multiple = group.realEstates.length > 1;
  const starsValue = changed > 0 && effective !== null ? Math.round(effective) : (entry.base as number | null);

  return (
    <div className={classes.block}>
      <Paper id={id} variant="outlined" className={classes.card}>
        {flash && <span className={classes.flash} />}
        <div className={classes.text}>
          <Typography className={classes.label}>{criterion.criteriaRatingName}</Typography>
          {changed > 0 && (
            <span className={classes.badge}>{badgeText(changed, group.realEstates.length, effective, isStars)}</span>
          )}
        </div>

        {isStars ? (
          <StarsValue
            name={`${id}-base`}
            value={starsValue}
            size="large"
            label={changed > 0 ? String(effective) : null}
            onChange={onBase}
          />
        ) : (
          <YesNoValue value={entry.base as boolean | null} size="medium" onChange={onBase} />
        )}

        {multiple && (
          <Button
            size="small"
            color="primary"
            startIcon={<HomeOutlined />}
            className={classes.link}
            onClick={onToggleRealEstates}
          >
            {realEstatesOpen ? 'Скрыть' : 'По ЖК'} ({group.realEstates.length})
          </Button>
        )}
      </Paper>

      {multiple && (
        <Collapse in={realEstatesOpen} unmountOnExit>
          <div className={classes.overrides}>
            <div className={classes.overridesHead}>
              <Typography className={classes.overridesTitle}>
                Уточнение по ЖК · без изменений ЖК наследует общую оценку
              </Typography>
              {changed > 0 && (
                <Link component="button" className={classes.reset} onClick={onResetOverrides}>
                  Сбросить уточнения
                </Link>
              )}
            </div>

            {group.realEstates.map(({ realEstateGuid, realEstateName }) => {
              const own = realEstateGuid in entry.overrides;
              const value = valueFor(entry, realEstateGuid);
              const handleChange = (next: CriterionValue) => onOverride(realEstateGuid, next);

              return (
                <div key={realEstateGuid} className={classes.overrideRow}>
                  <Typography className={classes.realEstate}>{realEstateName}</Typography>
                  {own && <span className={classes.changed}>изменено</span>}
                  {isStars ? (
                    <StarsValue
                      name={`${id}-${realEstateGuid}`}
                      value={value as number | null}
                      size="small"
                      dim={!own}
                      onChange={handleChange}
                    />
                  ) : (
                    <YesNoValue value={value as boolean | null} size="small" onChange={handleChange} />
                  )}
                </div>
              );
            })}
          </div>
        </Collapse>
      )}
    </div>
  );
};

export default PartnerCriterionCard;
